// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Curry from "rescript/lib/es6/curry.js";
import * as Js_exn from "rescript/lib/es6/js_exn.js";
import * as Belt_List from "rescript/lib/es6/belt_List.js";
import * as Belt_Array from "rescript/lib/es6/belt_Array.js";
import * as Pervasives from "rescript/lib/es6/pervasives.js";
import * as Dom_storage from "rescript/lib/es6/dom_storage.js";
import * as Caml_js_exceptions from "rescript/lib/es6/caml_js_exceptions.js";

function absFloat(value) {
  if (value < 0.0) {
    return value * -1.0;
  } else {
    return value;
  }
}

function withCommas(v) {
  var __x = Belt_List.reverse(Belt_List.fromArray(v.split("")));
  return Belt_Array.joinWith(Belt_List.toArray(Belt_List.reduceWithIndex(__x, /* [] */0, (function (a, b, index) {
                        if (index % 3 === 0 && index > 0) {
                          return {
                                  hd: b,
                                  tl: {
                                    hd: ",",
                                    tl: a
                                  }
                                };
                        } else {
                          return {
                                  hd: b,
                                  tl: a
                                };
                        }
                      }))), "", (function (a) {
                return a;
              }));
}

function withCommasFloat(value, fraction) {
  var tokens = Belt_List.fromArray(value.toString().split("."));
  if (Belt_List.length(tokens) === 1) {
    return withCommas(Belt_List.headExn(tokens));
  }
  var trunked = value.toFixed(fraction);
  var tokens$1 = Belt_List.fromArray(trunked.split("."));
  if (Belt_List.length(tokens$1) === 1) {
    return withCommas(Belt_List.headExn(tokens$1));
  } else {
    return withCommas(Belt_List.headExn(tokens$1)) + "." + Belt_List.headExn(Belt_List.tailExn(tokens$1));
  }
}

var Numbers = {
  absFloat: absFloat,
  withCommas: withCommas,
  withCommasFloat: withCommasFloat
};

function stringToEpochTime(x) {
  return new Date(x).getTime();
}

function now(param) {
  return Date.now();
}

function addPadding(x) {
  var intX = x | 0;
  var match = intX >= 10;
  var match$1 = intX < 10 && intX >= 0;
  var match$2 = intX < 0 && intX > -10;
  var match$3 = intX <= -10;
  if (match) {
    return String(intX);
  }
  if (match$1) {
    return "0" + String(intX);
  }
  if (match$2) {
    return "-0" + String(Pervasives.abs(intX));
  }
  if (match$3) {
    return "-" + String(Pervasives.abs(intX));
  }
  throw {
        RE_EXN_ID: "Invalid_argument",
        _1: "Number out of range, should not happen",
        Error: new Error()
      };
}

function hourMinSecToString(hourMinSec) {
  return addPadding(hourMinSec.hour) + ":" + addPadding(hourMinSec.min) + ":" + addPadding(hourMinSec.sec);
}

function minSecToString(hourMinSec) {
  return addPadding(hourMinSec.min) + ":" + addPadding(hourMinSec.sec);
}

function calculateDiffToSpecificTime(startTime, endTime) {
  var nowDate = startTime / 1000.0;
  var endDate = endTime / 1000.0;
  if (nowDate > endDate) {
    return /* TimeReached */0;
  }
  var delta = Math.floor(endDate - nowDate);
  var checkDays = delta / 86400.0;
  var daysFloat = Math.floor(checkDays);
  var hourFloat = Math.floor((delta - daysFloat * 86400.0) / 3600.0) % 24.0;
  var minFloat = Math.floor((delta - (hourFloat * 3600.0 - daysFloat * 86400.0)) / 60.0) % 60.0;
  var secFloat = Math.floor((delta - (minFloat * 60.0 - hourFloat * 3600.0 - daysFloat * 86400.0)) % 60.0);
  var intOfDays = daysFloat | 0;
  if (intOfDays > 0) {
    return {
            TAG: /* TimeUntilReached */2,
            _0: {
              day: intOfDays,
              hour: hourFloat,
              min: minFloat,
              sec: secFloat
            }
          };
  } else {
    return {
            TAG: /* LessThen24hours */0,
            _0: {
              hour: hourFloat,
              min: minFloat,
              sec: secFloat
            }
          };
  }
}

function calculateDiff(startTime, endTime) {
  var nowDate = startTime / 1000.0;
  var endDate = endTime / 1000.0;
  if (nowDate > endDate) {
    return /* TimeReached */0;
  }
  var roundDays = function (x) {
    if (x < 0) {
      return Math.ceil(x - 0.5);
    } else {
      return Math.floor(x + 0.5);
    }
  };
  var delta = Math.floor(endDate - nowDate);
  var checkDays = delta / 86400.0;
  var daysFloat = checkDays >= 1.0 ? roundDays(checkDays) : Math.floor(checkDays);
  var hourFloat = Math.floor((delta - daysFloat * 86400.0) / 3600.0) % 24.0;
  var minFloat = Math.floor((delta - (hourFloat * 3600.0 - daysFloat * 86400.0)) / 60.0) % 60.0;
  var secFloat = Math.floor((delta - (minFloat * 60.0 - hourFloat * 3600.0 - daysFloat * 86400.0)) % 60.0);
  var intOfDays = daysFloat | 0;
  if (intOfDays > 0) {
    return {
            TAG: /* DaysUntil */1,
            _0: intOfDays
          };
  } else {
    return {
            TAG: /* LessThen24hours */0,
            _0: {
              hour: hourFloat,
              min: minFloat,
              sec: secFloat
            }
          };
  }
}

function percentageLeftofOneMinute(hourMinSec) {
  var timeLeftInSeconds = hourMinSec.min * 60.0 + hourMinSec.sec;
  return timeLeftInSeconds / 60.0 * 100.0;
}

var Timer = {
  stringToEpochTime: stringToEpochTime,
  now: now,
  addPadding: addPadding,
  hourMinSecToString: hourMinSecToString,
  minSecToString: minSecToString,
  calculateDiffToSpecificTime: calculateDiffToSpecificTime,
  calculateDiff: calculateDiff,
  percentageLeftofOneMinute: percentageLeftofOneMinute
};

function MakeLocalStorage(T) {
  var getItem = function (key) {
    try {
      return Dom_storage.getItem(Curry._1(T.keyToJs, key), localStorage);
    }
    catch (raw__e){
      var _e = Caml_js_exceptions.internalToOCamlException(raw__e);
      if (_e.RE_EXN_ID === Js_exn.$$Error) {
        return ;
      }
      throw _e;
    }
  };
  var remove = function (key) {
    try {
      return Dom_storage.removeItem(Curry._1(T.keyToJs, key), localStorage);
    }
    catch (exn){
      return ;
    }
  };
  var setItem = function (key, value) {
    try {
      return Dom_storage.setItem(Curry._1(T.keyToJs, key), value, localStorage);
    }
    catch (raw__e){
      var _e = Caml_js_exceptions.internalToOCamlException(raw__e);
      if (_e.RE_EXN_ID === Js_exn.$$Error) {
        return ;
      }
      throw _e;
    }
  };
  return {
          keys: T.keys,
          getItem: getItem,
          remove: remove,
          setItem: setItem
        };
}

function MakeSessionStorage(T) {
  var getItem = function (key) {
    try {
      return Dom_storage.getItem(Curry._1(T.keyToJs, key), sessionStorage);
    }
    catch (raw__e){
      var _e = Caml_js_exceptions.internalToOCamlException(raw__e);
      if (_e.RE_EXN_ID === Js_exn.$$Error) {
        return ;
      }
      throw _e;
    }
  };
  var remove = function (key) {
    try {
      return Dom_storage.removeItem(Curry._1(T.keyToJs, key), sessionStorage);
    }
    catch (exn){
      return ;
    }
  };
  var setItem = function (key, value) {
    try {
      return Dom_storage.setItem(Curry._1(T.keyToJs, key), value, sessionStorage);
    }
    catch (raw__e){
      var _e = Caml_js_exceptions.internalToOCamlException(raw__e);
      if (_e.RE_EXN_ID === Js_exn.$$Error) {
        return ;
      }
      throw _e;
    }
  };
  return {
          keys: T.keys,
          getItem: getItem,
          remove: remove,
          setItem: setItem
        };
}

var Web = {
  MakeLocalStorage: MakeLocalStorage,
  MakeSessionStorage: MakeSessionStorage
};

export {
  Numbers ,
  Timer ,
  Web ,
}
/* No side effect */
